import { mapify } from "@asmbl/shared/utils";
import fastDeepEqual from "fast-deep-equal";
import {
  CompCycleSingleRecModal_recItem as RecItem,
  RecItem2,
  RecItemInput,
} from "../../__generated__/graphql";
import { CompRecommendation } from "../../models/CompRecommendation";

export function getSubmittedRecommendation(employee: {
  id: number;
  compRecommendation: {
    latestSubmittedItems: (RecItem | RecItem2)[];
    canICurrentlyEdit: boolean;
  } | null;
}): CompRecommendation | undefined {
  const currentEmployeeRec = employee.compRecommendation;

  if (!currentEmployeeRec) return;

  // We only want the RecItems from the most recent submission. It's important
  // that we format the submitted items to match the RecItemInput format to be
  // able to compare if data has changed.
  const lastSubmittedItems: RecItemInput[] =
    currentEmployeeRec.latestSubmittedItems.map(toRecItemInput);

  return {
    items: mapify(lastSubmittedItems, "recommendationType"),
    subjectId: employee.id,
    canICurrentlyEdit: currentEmployeeRec.canICurrentlyEdit,
  };
}

/**
 * Converts a RecItem to a RecItemInput. Be warned: don't use the spread syntax
 * here, or you'll break the mutation.
 */
export function toRecItemInput({
  recommendationType,
  recommendedPosition,
  recommendedCashValue,
  recommendedPercentValue,
  recommendedTitle,
  note,
  unitType,
}: RecItem): RecItemInput {
  return {
    recommendationType,
    recommendedCashValue,
    recommendedPercentValue,
    unitType,
    note,
    recommendedPositionId: recommendedPosition?.id,
    recommendedTitle,
  };
}

export function recommendationHasBeenTouched(
  current: CompRecommendation,
  original: CompRecommendation | undefined | null,
  canEditInCurrentPhase: boolean
): boolean {
  // If the original recommendation is undefined then we have never made this
  // recommendation so it has been touched
  if (original === undefined || original === null) return true;

  for (const [currentRecItemType, currentRecItem] of current.items) {
    const originalRecItem = original.items.get(currentRecItemType);

    if (!fastDeepEqual(currentRecItem, originalRecItem)) return true;
  }

  /* within a phase, the user can submit existing items without changes if they have edit permissions, since they are either
   ** 1) acting on guidance from an admin or
   ** 2) are in a phase with multiple reviewers
   */
  if (original.items.size > 0 && canEditInCurrentPhase) return true;

  return false;
}
